import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Hash, Pencil, Send, Trash2, X, Paperclip, SmilePlus, AtSign, Reply, CornerDownLeft, FileText, Music, Film } from "lucide-react";

import { useAuth } from "@/auth/AuthContext";
import {
  getCourses,
  getMyEnrollments,
  getTeachingCourses,
  getCourseStudents,
  getCourseTeachers,
  type Course,
} from "@/api/lms/courses";
import {
  getCourseCommunity,
  getCourseCommunityPosts,
  getCourseCommunityMembers,
  addCourseCommunityMember,
  removeCourseCommunityMember,
  deleteCourseCommunityPost,
  updateCourseCommunityPost,
  uploadCourseCommunityPostAttachment,
  type CommunityPost,
} from "@/api/lms/community";
import { useCommunitySocketContext } from "@/realtime/CommunitySocketProvider";

import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatRelativeTime, resolveInstantMs } from "@/lib/formatRelative";
import { cn } from "@/lib/utils";

const POSTS_LIMIT = 20;

// ─── Emoji Data ─────────────────────────────────────────────────────────────

const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  {
    label: "Smileys",
    emojis: ["😊", "😂", "🥰", "😍", "🤩", "😎", "🤔", "😅", "😭", "😤", "🥺", "😏", "🙄", "😴", "🤯", "🤗"],
  },
  {
    label: "Gestures",
    emojis: ["👍", "👎", "👏", "🙌", "🤝", "🙏", "💪", "✌️", "🤞", "👌", "🤙", "💯"],
  },
  {
    label: "Hearts",
    emojis: ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💔", "💖", "💗", "💝"],
  },
  {
    label: "Learning",
    emojis: ["📚", "📖", "✏️", "📝", "🎓", "🏫", "💡", "🔍", "📊", "📈", "🗓️", "⏰"],
  },
  {
    label: "Japanese",
    emojis: ["🎌", "🗾", "🏯", "⛩️", "🍣", "🍜", "🌸", "🎋", "🎎", "🎏", "🎐", "🗻"],
  },
  {
    label: "Celebrate",
    emojis: ["🎉", "🎊", "🥳", "🎈", "🎁", "🏆", "🥇", "🌟", "⭐", "✨", "🔥", "💥"],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function communityPostOrder(a: CommunityPost, b: CommunityPost): number {
  const at = resolveInstantMs(a.createdAt, a.createdAtMs);
  const bt = resolveInstantMs(b.createdAt, b.createdAtMs);
  return (Number.isNaN(at) ? 0 : at) - (Number.isNaN(bt) ? 0 : bt);
}

function isPostEdited(post: CommunityPost): boolean {
  const c = post.createdAtMs;
  const u = post.updatedAtMs;
  return u != null && c != null && Number.isFinite(u) && Number.isFinite(c) && u > c + 1000;
}

function getDisplayName(post: CommunityPost): string {
  const profile = post.user?.profile ?? null;
  const fullName = [profile?.firstName ?? "", profile?.lastName ?? ""].join(" ").trim();
  return fullName || (post.user?.email ? post.user.email.split("@")[0] : `User ${post.userId}`);
}

function getUsernameSlug(post: CommunityPost): string {
  const profile = post.user?.profile ?? null;
  const fullName = [profile?.firstName ?? "", profile?.lastName ?? ""].join(" ").trim();
  if (fullName) return fullName.toLowerCase().replace(/\s+/g, ".");
  return post.user?.email?.split("@")[0] ?? `user${post.userId}`;
}

function insertAtCursor(
  el: HTMLTextAreaElement,
  text: string,
): string {
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  const val = el.value;
  return val.slice(0, start) + text + val.slice(end);
}

/** Render post content with @mention highlights */
function renderContentWithMentions(content: string): React.ReactNode {
  if (!content) return null;
  const parts = content.split(/(@\S+)/g);
  return parts.map((part, i) => {
    if (part.startsWith("@")) {
      return (
        <span
          key={i}
          className="mention-chip"
        >
          {part}
        </span>
      );
    }
    return part;
  });
}

// ─── Sub-components ─────────────────────────────────────────────────────────

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
}

function EmojiPicker({ onSelect, onClose }: EmojiPickerProps) {
  const [activeCategory, setActiveCategory] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="community-emoji-picker"
      role="dialog"
      aria-label="Emoji picker"
    >
      {/* Category tabs */}
      <div className="emoji-category-tabs">
        {EMOJI_CATEGORIES.map((cat, i) => (
          <button
            key={cat.label}
            type="button"
            aria-label={cat.label}
            onClick={() => setActiveCategory(i)}
            className={cn("emoji-cat-tab", i === activeCategory && "active")}
          >
            {cat.emojis[0]}
          </button>
        ))}
      </div>
      {/* Emoji grid */}
      <div className="emoji-grid" role="listbox">
        {EMOJI_CATEGORIES[activeCategory]?.emojis.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className="emoji-btn"
            role="option"
            aria-selected={false}
            onClick={() => {
              onSelect(emoji);
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
      <p className="emoji-cat-label">{EMOJI_CATEGORIES[activeCategory]?.label}</p>
    </div>
  );
}

interface MentionDropdownProps {
  query: string;
  candidates: { userId: number; displayName: string; username: string }[];
  onSelect: (username: string) => void;
  onClose: () => void;
  showAll: boolean;
}

function MentionDropdown({ query, candidates, onSelect, onClose, showAll }: MentionDropdownProps) {
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    const results = candidates.filter(
      (c) => c.displayName.toLowerCase().includes(q) || c.username.toLowerCase().includes(q)
    );
    return results.slice(0, 8);
  }, [query, candidates]);

  const allItems = useMemo(() => {
    const items: { type: "all" | "user"; label: string; username: string }[] = [];
    if (showAll && ("all".includes(query.toLowerCase()) || "everyone".includes(query.toLowerCase()) || query === "")) {
      items.push({ type: "all", label: "Everyone", username: "all" });
    }
    for (const c of filtered) {
      items.push({ type: "user", label: c.displayName, username: c.username });
    }
    return items;
  }, [filtered, query, showAll]);

  if (allItems.length === 0) return null;

  return (
    <div className="mention-dropdown" role="listbox" aria-label="Mention users">
      {allItems.map((item) => (
        <button
          key={item.username}
          type="button"
          role="option"
          aria-selected={false}
          className="mention-item"
          onMouseDown={(e) => {
            e.preventDefault(); // don't blur textarea
            onSelect(item.username);
          }}
        >
          <div className={cn("mention-avatar", item.type === "all" && "mention-avatar-all")}>
            {item.type === "all" ? "@" : item.label.charAt(0).toUpperCase()}
          </div>
          <div className="mention-info">
            <span className="mention-name">{item.label}</span>
            <span className="mention-handle">@{item.username}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function CommunityPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const backendRole = user?.backendRole;
  const isAdmin = backendRole === "SYSTEM_ADMIN";
  const meId = user?.id ?? null;

  const selectedCourseIdParam = searchParams.get("courseId");

  const studentEnrollmentsQuery = useQuery({
    queryKey: ["myEnrollmentsForCommunity"],
    queryFn: getMyEnrollments,
    enabled: backendRole === "STUDENT",
  });

  const teacherCoursesQuery = useQuery({
    queryKey: ["myTeachingCoursesForCommunity"],
    queryFn: getTeachingCourses,
    enabled: backendRole === "TEACHER",
  });

  const adminCoursesQuery = useQuery({
    queryKey: ["allCoursesForCommunity"],
    queryFn: () => getCourses({ page: 1, limit: 50 }),
    enabled: backendRole === "SYSTEM_ADMIN",
  });

  const enrollments = studentEnrollmentsQuery.data ?? [];

  const courseOptions = useMemo<Course[]>(() => {
    if (backendRole === "STUDENT") {
      return (enrollments ?? []).map((e: any) => e.course as Course).filter(Boolean);
    }
    if (backendRole === "TEACHER") return teacherCoursesQuery.data ?? [];
    if (backendRole === "SYSTEM_ADMIN") return adminCoursesQuery.data?.items ?? [];
    return [];
  }, [backendRole, enrollments, teacherCoursesQuery.data, adminCoursesQuery.data]);

  const resolvedCourseId = useMemo(() => {
    if (selectedCourseIdParam) return selectedCourseIdParam;
    return courseOptions?.[0]?.id ?? null;
  }, [selectedCourseIdParam, courseOptions]);

  useEffect(() => {
    if (!resolvedCourseId) return;
    if (selectedCourseIdParam) return;
    setSearchParams({ courseId: resolvedCourseId });
  }, [resolvedCourseId, selectedCourseIdParam, setSearchParams]);

  const courseSelectorLabel =
    backendRole === "TEACHER"
      ? "Teaching"
      : backendRole === "SYSTEM_ADMIN"
        ? "All"
        : "My";

  const communityQuery = useQuery({
    queryKey: ["communityForCourse", resolvedCourseId],
    enabled: Boolean(resolvedCourseId),
    queryFn: () => getCourseCommunity(resolvedCourseId as string),
  });

  const postsQuery = useQuery({
    queryKey: ["communityPosts", resolvedCourseId],
    enabled: Boolean(resolvedCourseId),
    queryFn: () =>
      getCourseCommunityPosts({
        courseId: resolvedCourseId as string,
        pagination: { page: 1, limit: POSTS_LIMIT },
      }),
  });

  const communityId = communityQuery.data?.id ?? null;

  // Admin: manage community members (manual add/remove).
  const communityMembersQuery = useQuery({
    queryKey: ["communityMembersForCourse", resolvedCourseId],
    enabled: isAdmin && Boolean(resolvedCourseId),
    queryFn: () => getCourseCommunityMembers(resolvedCourseId as string),
  });

  const courseStudentsQuery = useQuery({
    queryKey: ["courseStudentsForCommunityAdmin", resolvedCourseId],
    enabled: isAdmin && Boolean(resolvedCourseId),
    queryFn: () => getCourseStudents(resolvedCourseId as string),
  });

  const courseTeachersQuery = useQuery({
    queryKey: ["courseTeachersForCommunityAdmin", resolvedCourseId],
    enabled: isAdmin && Boolean(resolvedCourseId),
    queryFn: () => getCourseTeachers(resolvedCourseId as string),
  });

  const members = communityMembersQuery.data ?? [];
  const memberIdSet = useMemo(() => new Set(members.map((m) => m.userId)), [members]);

  const candidates = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of courseStudentsQuery.data ?? []) {
      map.set(s.userId, s.user?.email ?? `User ${s.userId}`);
    }
    for (const t of courseTeachersQuery.data ?? []) {
      map.set(t.teacherId, t.teacher?.email ?? `User ${t.teacherId}`);
    }
    return Array.from(map.entries())
      .filter(([userId]) => !memberIdSet.has(userId))
      .map(([userId, email]) => ({ userId, email }));
  }, [courseStudentsQuery.data, courseTeachersQuery.data, memberIdSet]);

  const [candidateUserId, setCandidateUserId] = useState<number | null>(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [candidateSearch, setCandidateSearch] = useState("");

  useEffect(() => {
    setCandidateUserId(null);
  }, [resolvedCourseId]);

  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const postsContainerRef = useRef<HTMLDivElement | null>(null);
  const postsCourseIdRef = useRef<string | null>(null);

  useEffect(() => {
    const items = postsQuery.data?.items ?? [];
    const fromServer = [...items].reverse();

    if (postsCourseIdRef.current !== resolvedCourseId) {
      postsCourseIdRef.current = resolvedCourseId;
      setPosts(fromServer);
      return;
    }

    setPosts((prev) => {
      const byId = new Map<string, CommunityPost>();
      for (const p of fromServer) byId.set(p.id, p);
      for (const p of prev) {
        if (!byId.has(p.id)) byId.set(p.id, p);
      }
      return Array.from(byId.values()).sort(communityPostOrder);
    });
  }, [postsQuery.data, resolvedCourseId]);

  useLayoutEffect(() => {
    const el = postsContainerRef.current;
    if (!el) return;
    const scrollToBottom = () => { el.scrollTop = el.scrollHeight; };
    scrollToBottom();
    requestAnimationFrame(scrollToBottom);
  }, [posts]);

  // ─── Mention candidates from posts feed ─────────────────────────────────
  const mentionCandidates = useMemo(() => {
    const seen = new Map<number, { userId: number; displayName: string; username: string }>();
    for (const post of posts) {
      if (!post.user) continue;
      if (!seen.has(post.userId)) {
        seen.set(post.userId, {
          userId: post.userId,
          displayName: getDisplayName(post),
          username: getUsernameSlug(post),
        });
      }
    }
    // Also add current user
    if (meId && user && !seen.has(meId)) {
      const email = (user as any).email ?? "";
      seen.set(meId, {
        userId: meId,
        displayName: email.split("@")[0] ?? `User ${meId}`,
        username: email.split("@")[0] ?? `user${meId}`,
      });
    }
    return Array.from(seen.values());
  }, [posts, meId, user]);

  // ─── Composer State ──────────────────────────────────────────────────────
  const [content, setContent] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState<string | null>(null);
  const [attachmentPreviewType, setAttachmentPreviewType] = useState<
    "IMAGE" | "VIDEO" | "PDF" | "AUDIO" | null
  >(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [replyTarget, setReplyTarget] = useState<CommunityPost | null>(null);

  // Mention system state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null); // null = picker closed
  const composerContainerRef = useRef<HTMLDivElement | null>(null);

  const { socket, isConnected, joinCourse, sendPost } = useCommunitySocketContext();

  // Ensure we're in the selected course room (membership enforced server-side).
  useEffect(() => {
    if (!socket || !isConnected || !resolvedCourseId) return;
    void joinCourse(resolvedCourseId).catch(() => undefined);
  }, [socket, isConnected, resolvedCourseId, joinCourse]);

  // Realtime posts: append to bottom, keep chronological order.
  useEffect(() => {
    if (!socket) return;

    const handler = (post: CommunityPost) => {
      if (communityId && post.communityId !== communityId) return;
      setPosts((prev) => {
        if (prev.some((p) => p.id === post.id)) return prev;
        const next = [...prev, post].slice(0, 200);
        next.sort(communityPostOrder);
        return next;
      });
    };

    socket.on("community:post-created", handler);
    return () => { socket.off("community:post-created", handler); };
  }, [socket, communityId]);

  // Realtime deletion
  useEffect(() => {
    if (!socket) return;

    const handler = (payload: { postId: string }) => {
      const { postId } = payload ?? {};
      if (!postId) return;
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    };

    socket.on("community:post-deleted", handler);
    return () => { socket.off("community:post-deleted", handler); };
  }, [socket]);

  // Realtime update
  useEffect(() => {
    if (!socket) return;

    const handler = (post: CommunityPost) => {
      if (communityId && post.communityId !== communityId) return;
      setPosts((prev) => {
        const ix = prev.findIndex((p) => p.id === post.id);
        if (ix === -1) return prev;
        const next = [...prev];
        next[ix] = { ...next[ix], ...post };
        return next.sort(communityPostOrder);
      });
    };

    socket.on("community:post-updated", handler);
    return () => { socket.off("community:post-updated", handler); };
  }, [socket, communityId]);

  // ─── Textarea auto-resize ────────────────────────────────────────────────
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 144) + "px"; // max ~6 lines
  }, [content]);

  const inferAttachmentTypeFromFile = (file: File): "IMAGE" | "VIDEO" | "PDF" | "AUDIO" | null => {
    const t = file.type;
    if (t.startsWith("image/")) return "IMAGE";
    if (t === "application/pdf") return "PDF";
    if (t.startsWith("video/")) return "VIDEO";
    if (t.startsWith("audio/")) return "AUDIO";
    return null;
  };

  const clearAttachment = useCallback(() => {
    if (attachmentPreviewUrl) URL.revokeObjectURL(attachmentPreviewUrl);
    setAttachmentFile(null);
    setAttachmentPreviewUrl(null);
    setAttachmentPreviewType(null);
  }, [attachmentPreviewUrl]);

  // ─── Mention handling ────────────────────────────────────────────────────
  const handleContentChange = (val: string) => {
    setContent(val);

    // Detect @mention trigger
    const el = textareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? val.length;
    const textBeforeCursor = val.slice(0, cursor);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
    } else {
      setMentionQuery(null);
    }
  };

  const insertMention = (username: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? content.length;
    const textBeforeCursor = content.slice(0, cursor);
    const textAfterCursor = content.slice(cursor);

    // Replace the @<partial> with the full mention
    const replaced = textBeforeCursor.replace(/@(\w*)$/, `@${username} `);
    const newContent = replaced + textAfterCursor;
    setContent(newContent);
    setMentionQuery(null);

    // Restore focus + move cursor after inserted mention
    setTimeout(() => {
      el.focus();
      const newCursor = replaced.length;
      el.setSelectionRange(newCursor, newCursor);
    }, 0);
  };

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    if (!el) {
      setContent((prev) => prev + emoji);
      return;
    }
    const newVal = insertAtCursor(el, emoji);
    setContent(newVal);
    // Move cursor after emoji
    const pos = (el.selectionStart ?? 0) + emoji.length;
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    }, 0);
    setShowEmojiPicker(false);
  };

  // ─── Send ────────────────────────────────────────────────────────────────
  const onSend = useCallback(async () => {
    if (!resolvedCourseId) return;

    const trimmedContent = content.trim();

    // Validation: must have content OR attachment
    if (!trimmedContent && !attachmentFile) {
      toast.error("Please enter a message or attach a file.");
      return;
    }

    if (!isConnected || !socket) {
      toast.error("Realtime connection not ready. Try again in a moment.");
      return;
    }

    try {
      setIsUploadingAttachment(true);

      let attachmentUpload:
        | { url: string; attachmentType: "IMAGE" | "VIDEO" | "PDF" | "AUDIO" }
        | null = null;

      if (attachmentFile) {
        attachmentUpload = await uploadCourseCommunityPostAttachment(resolvedCourseId, attachmentFile);
      }

      const created = await (sendPost as any)({
        courseId: resolvedCourseId,
        content: trimmedContent,
        parentId: replyTarget?.id ?? null,
        attachmentUrl: attachmentUpload?.url ?? null,
        attachmentType: attachmentUpload?.attachmentType ?? null,
      });

      setPosts((prev) => {
        if (prev.some((p) => p.id === created.id)) return prev;
        const next = [...prev, created].slice(0, 200);
        next.sort(communityPostOrder);
        return next;
      });

      void queryClient.invalidateQueries({ queryKey: ["communityPosts", resolvedCourseId] });

      setContent("");
      clearAttachment();
      setReplyTarget(null);
      setShowEmojiPicker(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to send community post");
    } finally {
      setIsUploadingAttachment(false);
    }
  }, [
    content,
    resolvedCourseId,
    sendPost,
    isConnected,
    socket,
    attachmentFile,
    clearAttachment,
    queryClient,
    replyTarget,
  ]);

  const onDeletePost = useCallback(
    async (post: CommunityPost) => {
      if (!resolvedCourseId) return;
      if (!meId) return;

      const canModify = isAdmin || post.userId === meId;
      if (!canModify) return;

      try {
        const resp = await deleteCourseCommunityPost(resolvedCourseId, post.id);
        if (resp?.removed) {
          setPosts((prev) => prev.filter((p) => p.id !== post.id));
        } else {
          toast.error("Failed to delete message.");
        }
      } catch (e: any) {
        toast.error(e?.message ?? "Failed to delete message.");
      }
    },
    [resolvedCourseId, meId, isAdmin],
  );

  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState<CommunityPost | null>(null);
  const [isDeletingPost, setIsDeletingPost] = useState(false);

  const [editTarget, setEditTarget] = useState<CommunityPost | null>(null);
  const [editContent, setEditContent] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  useEffect(() => {
    if (editTarget) setEditContent(editTarget.content);
  }, [editTarget]);

  const onSaveEdit = useCallback(async () => {
    if (!resolvedCourseId || !editTarget) return;
    const trimmed = editContent.trim();
    if (!trimmed) return;
    try {
      setIsSavingEdit(true);
      const updated = await updateCourseCommunityPost(resolvedCourseId, editTarget.id, {
        content: trimmed,
      });
      setPosts((prev) =>
        prev
          .map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
          .sort(communityPostOrder),
      );
      void queryClient.invalidateQueries({ queryKey: ["communityPosts", resolvedCourseId] });
      setEditTarget(null);
      toast.success("Message updated");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to update message");
    } finally {
      setIsSavingEdit(false);
    }
  }, [resolvedCourseId, editTarget, editContent, queryClient]);

  const isCoursesLoading =
    backendRole === "STUDENT"
      ? studentEnrollmentsQuery.isPending
      : backendRole === "TEACHER"
        ? teacherCoursesQuery.isPending
        : backendRole === "SYSTEM_ADMIN"
          ? adminCoursesQuery.isPending
          : false;

  const isLoading =
    isCoursesLoading ||
    (Boolean(resolvedCourseId) && (communityQuery.isPending || postsQuery.isPending));


  // ─── Post map for reply lookups ───────────────────────────────────────────
  const postById = useMemo(() => {
    const map = new Map<string, CommunityPost>();
    for (const p of posts) map.set(p.id, p);
    return map;
  }, [posts]);

  // Split posts: root posts + their replies grouped
  const rootPosts = posts.filter((p) => !p.parentId);
  const repliesByParent = useMemo(() => {
    const map = new Map<string, CommunityPost[]>();
    for (const p of posts) {
      if (p.parentId) {
        const arr = map.get(p.parentId) ?? [];
        arr.push(p);
        map.set(p.parentId, arr);
      }
    }
    return map;
  }, [posts]);

  if (!user) return null;

  const coursesLoadError =
    backendRole === "STUDENT"
      ? studentEnrollmentsQuery.isError
      : backendRole === "TEACHER"
        ? teacherCoursesQuery.isError
        : backendRole === "SYSTEM_ADMIN"
          ? adminCoursesQuery.isError
          : false;

  if (coursesLoadError) {
    return (
      <DashboardLayout>
        <div className="dash-page flex items-center justify-center">
          <div className="dash-empty-state">
            <p className="font-semibold text-foreground text-lg mb-2">Failed to load courses</p>
            <p className="text-sm text-muted-foreground">
              Please refresh or try selecting a course via the URL query parameter `courseId`.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (communityQuery.isError || postsQuery.isError) {
    return (
      <DashboardLayout>
        <div className="dash-page flex items-center justify-center">
          <div className="dash-empty-state">
            <p className="font-semibold text-foreground text-lg mb-2">Failed to load community</p>
            <p className="text-sm text-muted-foreground">
              You might not have access to this course community yet.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex flex-1 min-h-0 overflow-hidden">
          <div className="hidden md:block w-56 border-r border-border p-4 space-y-4">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="flex-1 flex flex-col min-w-0">
            <div className="p-4 border-b border-border">
              <Skeleton className="h-6 w-64" />
            </div>
            <div className="flex-1 p-6 space-y-4 overflow-y-auto min-h-0">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="p-4">
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!resolvedCourseId) {
    return (
      <DashboardLayout>
        <div className="dash-page flex items-center justify-center">
          <div className="dash-empty-state">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Hash size={28} className="text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-foreground text-lg mb-2">No courses found</h3>
            <p className="text-sm text-muted-foreground">
              Enroll in a course to join its community.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const activeCourseTitle =
    courseOptions.find((c) => c.id === resolvedCourseId)?.title ?? "Community";

  const canSend = (content.trim().length > 0 || Boolean(attachmentFile)) && isConnected && !isUploadingAttachment;

  // ─── Render single post (reusable) ───────────────────────────────────────
  const renderPost = (post: CommunityPost, isReply = false) => {
    const displayName = getDisplayName(post);
    const profile = post.user?.profile ?? null;
    const authorLetter = (displayName?.[0] ?? "U").toUpperCase();
    const canModify = isAdmin || (meId != null && post.userId === meId);
    const replyCount = repliesByParent.get(post.id)?.length ?? 0;

    return (
      <div key={post.id} className={cn("community-post-wrapper", isReply && "community-post-reply")}>
        <div className={cn("community-post group", isReply && "community-post-is-reply")}>
          {/* Reply indicator line */}
          {isReply && <div className="reply-thread-line" aria-hidden="true" />}

          <div className="post-avatar-col">
            {profile?.image ? (
              <img
                src={profile.image}
                alt={displayName}
                className="post-avatar-img"
              />
            ) : (
              <div className="post-avatar-initials">
                {authorLetter}
              </div>
            )}
            {/* Thread line for root posts that have replies */}
            {!isReply && replyCount > 0 && (
              <div className="post-thread-line" aria-hidden="true" />
            )}
          </div>

          <div className="post-body">
            <div className="post-header">
              <div className="post-meta">
                <span className="post-author">{displayName}</span>
                <span className="post-time">
                  {formatRelativeTime(post.createdAt, post.createdAtMs)}
                </span>
                {isPostEdited(post) && (
                  <span className="post-edited">(edited)</span>
                )}
              </div>
              <div className="post-actions">
                {!isReply && (
                  <button
                    type="button"
                    aria-label="Reply to this message"
                    className="post-action-btn post-action-reply"
                    onClick={() => {
                      setReplyTarget(post);
                      setTimeout(() => textareaRef.current?.focus(), 50);
                    }}
                  >
                    <Reply size={13} />
                    <span>Reply</span>
                  </button>
                )}
                {canModify && (
                  <>
                    <button
                      type="button"
                      aria-label="Edit message"
                      className="post-action-btn"
                      onClick={() => setEditTarget(post)}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      aria-label="Delete message"
                      className="post-action-btn post-action-delete"
                      onClick={() => setDeleteConfirmTarget(post)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Content */}
            {post.content && (
              <p className="post-content">
                {renderContentWithMentions(post.content)}
              </p>
            )}

            {/* Attachment */}
            {post.attachmentUrl && post.attachmentType && (
              <div className="post-attachment">
                {post.attachmentType === "IMAGE" && (
                  <img
                    src={post.attachmentUrl}
                    alt="Community attachment"
                    className="post-attachment-image"
                  />
                )}
                {post.attachmentType === "VIDEO" && (
                  <video
                    src={post.attachmentUrl}
                    controls
                    className="post-attachment-video"
                  />
                )}
                {post.attachmentType === "PDF" && (
                  <a
                    href={post.attachmentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="post-attachment-file"
                  >
                    <FileText size={18} />
                    <span>View PDF</span>
                  </a>
                )}
                {post.attachmentType === "AUDIO" && (
                  <audio src={post.attachmentUrl} controls className="post-attachment-audio" />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Render replies for root posts */}
        {!isReply && (repliesByParent.get(post.id) ?? []).map((reply) =>
          renderPost(reply, true)
        )}
      </div>
    );
  };

  // ─── User initial / avatar for composer ──────────────────────────────────
  const myProfile = (user as any)?.profile ?? null;
  const myFirstName = myProfile?.firstName ?? "";
  const myEmail = (user as any)?.email ?? "";
  const myInitial = (myFirstName?.[0] ?? myEmail?.[0] ?? "U").toUpperCase();
  const myAvatarImg = myProfile?.image ?? null;

  return (
    <>
      {/* Community-specific styles injected into page */}
      <style>{`
        /* ── Layout ── */
        .community-layout { display: flex; flex: 1; min-height: 0; height: 100%; max-height: calc(100vh - 56px); overflow: hidden; }

        /* ── Sidebar ── */
        .community-sidebar { display: none; width: 17rem; min-height: 0; overflow-y: auto; border-right: 1px solid hsl(var(--border)); padding: 1rem; gap: 0; }
        @media (min-width: 768px) { .community-sidebar { display: flex; flex-direction: column; gap: 0.25rem; } }
        .sidebar-label { font-size: 0.6875rem; font-weight: 700; color: hsl(var(--muted-foreground)); text-transform: uppercase; letter-spacing: 0.08em; padding: 0 0.5rem; margin-bottom: 0.5rem; }
        .sidebar-course-btn { width: 100%; text-align: left; padding: 0.5rem 0.75rem; border-radius: 0.5rem; font-size: 0.8125rem; font-weight: 500; transition: background-color 0.15s, color 0.15s; display: flex; align-items: center; gap: 0.5rem; border: none; background: none; cursor: pointer; }
        .sidebar-course-btn.active { background: hsl(var(--primary) / 0.12); color: hsl(var(--primary)); }
        .sidebar-course-btn:not(.active) { color: hsl(var(--muted-foreground)); }
        .sidebar-course-btn:not(.active):hover { background: hsl(var(--secondary)); color: hsl(var(--foreground)); }

        /* ── Main area ── */
        .community-main { flex: 1; display: flex; flex-direction: column; min-width: 0; min-height: 0; height: 100%; overflow: hidden; }

        /* ── Header ── */
        .community-header { padding: 0.875rem 1.25rem; border-bottom: 1px solid hsl(var(--border)); display: flex; justify-content: space-between; align-items: center; gap: 1rem; background: hsl(var(--background)); flex-shrink: 0; }
        .community-header-title { display: flex; align-items: center; gap: 0.5rem; min-width: 0; }
        .community-header-title h2 { font-weight: 700; color: hsl(var(--foreground)); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        /* ── Posts feed ── */
        .community-feed { flex: 1; padding: 1.25rem; overflow-y: auto; min-height: 0; overscroll-behavior: contain; display: flex; flex-direction: column; gap: 0.25rem; }
        .feed-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; text-align: center; padding: 2.5rem; gap: 0.5rem; }
        .feed-empty-icon { width: 3.5rem; height: 3.5rem; border-radius: 1rem; background: hsl(var(--muted)); display: flex; align-items: center; justify-content: center; margin-bottom: 0.5rem; }

        /* ── Post ── */
        .community-post-wrapper { display: flex; flex-direction: column; }
        .community-post-reply { padding-left: 2.75rem; }
        .community-post { display: flex; gap: 0.625rem; padding: 0.5rem 0.5rem; border-radius: 0.75rem; transition: background-color 0.15s; position: relative; }
        .community-post:hover { background: hsl(var(--secondary) / 0.6); }
        .community-post.community-post-is-reply { padding-top: 0.375rem; padding-bottom: 0.375rem; }

        .post-avatar-col { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; }
        .post-avatar-img { width: 2.25rem; height: 2.25rem; border-radius: 50%; object-fit: cover; background: hsl(var(--primary) / 0.1); }
        .post-avatar-initials { width: 2.25rem; height: 2.25rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 700; background: hsl(var(--primary) / 0.12); color: hsl(var(--primary)); flex-shrink: 0; }
        .post-thread-line { width: 2px; flex: 1; min-height: 0.75rem; background: hsl(var(--border)); margin: 0.25rem auto 0; border-radius: 1px; }
        .reply-thread-line { display: none; }

        .post-body { min-width: 0; flex: 1; }
        .post-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.5rem; margin-bottom: 0.25rem; }
        .post-meta { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
        .post-author { font-weight: 700; font-size: 0.875rem; color: hsl(var(--foreground)); }
        .post-time { font-size: 0.75rem; color: hsl(var(--muted-foreground)); }
        .post-edited { font-size: 0.7rem; color: hsl(var(--muted-foreground) / 0.7); }

        .post-actions { display: flex; align-items: center; gap: 0.25rem; opacity: 0; transition: opacity 0.15s; flex-shrink: 0; }
        .community-post:hover .post-actions,
        .community-post:focus-within .post-actions { opacity: 1; }
        .post-action-btn { display: flex; align-items: center; gap: 0.25rem; padding: 0.25rem 0.5rem; border-radius: 0.375rem; font-size: 0.75rem; font-weight: 500; border: 1px solid hsl(var(--border)); background: hsl(var(--background)); color: hsl(var(--muted-foreground)); cursor: pointer; transition: all 0.15s; }
        .post-action-btn:hover { background: hsl(var(--secondary)); color: hsl(var(--foreground)); }
        .post-action-reply:hover { color: hsl(var(--primary)); border-color: hsl(var(--primary) / 0.3); background: hsl(var(--primary) / 0.06); }
        .post-action-delete:hover { color: hsl(var(--destructive)); border-color: hsl(var(--destructive) / 0.3); background: hsl(var(--destructive) / 0.06); }

        .post-content { font-size: 0.875rem; color: hsl(var(--foreground) / 0.85); line-height: 1.6; white-space: pre-wrap; word-break: break-word; }
        .mention-chip { display: inline-block; background: hsl(var(--primary) / 0.12); color: hsl(var(--primary)); font-weight: 600; border-radius: 0.25rem; padding: 0 0.25rem; font-size: 0.85em; }

        /* ── Attachments in posts ── */
        .post-attachment { margin-top: 0.5rem; }
        .post-attachment-image { max-width: min(280px, 100%); border-radius: 0.75rem; border: 1px solid hsl(var(--border)); display: block; }
        .post-attachment-video { max-width: min(360px, 100%); border-radius: 0.75rem; border: 1px solid hsl(var(--border)); background: black; display: block; }
        .post-attachment-audio { width: 100%; max-width: 320px; display: block; }
        .post-attachment-file { display: inline-flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0.875rem; border-radius: 0.5rem; border: 1px solid hsl(var(--border)); background: hsl(var(--secondary)); color: hsl(var(--foreground)); font-size: 0.8125rem; font-weight: 500; text-decoration: none; transition: all 0.15s; }
        .post-attachment-file:hover { background: hsl(var(--secondary) / 0.8); border-color: hsl(var(--primary) / 0.4); color: hsl(var(--primary)); }

        /* ── Composer ── */
        .community-composer { flex-shrink: 0; background: hsl(var(--background)); border-top: 1px solid hsl(var(--border)); padding: 0.75rem 1rem; position: relative; }

        /* Reply banner */
        .reply-banner { display: flex; align-items: center; gap: 0.5rem; padding: 0.375rem 0.75rem; margin-bottom: 0.5rem; border-radius: 0.5rem; background: hsl(var(--primary) / 0.07); border-left: 3px solid hsl(var(--primary)); }
        .reply-banner-icon { color: hsl(var(--primary)); flex-shrink: 0; }
        .reply-banner-text { flex: 1; font-size: 0.75rem; color: hsl(var(--muted-foreground)); overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
        .reply-banner-name { font-weight: 600; color: hsl(var(--primary)); }
        .reply-banner-close { padding: 0.125rem; border: none; background: none; cursor: pointer; color: hsl(var(--muted-foreground)); border-radius: 0.25rem; display: flex; align-items: center; }
        .reply-banner-close:hover { color: hsl(var(--foreground)); background: hsl(var(--secondary)); }

        /* Attachment preview in composer */
        .composer-attachment-preview { display: flex; align-items: center; gap: 0.625rem; padding: 0.5rem 0.75rem; margin-bottom: 0.5rem; border-radius: 0.625rem; background: hsl(var(--secondary)); border: 1px solid hsl(var(--border)); }
        .composer-attachment-thumb { width: 2.75rem; height: 2.75rem; border-radius: 0.375rem; overflow: hidden; flex-shrink: 0; background: hsl(var(--background)); display: flex; align-items: center; justify-content: center; border: 1px solid hsl(var(--border)); }
        .composer-attachment-thumb img { width: 100%; height: 100%; object-fit: cover; }
        .composer-attachment-icon { color: hsl(var(--muted-foreground)); }
        .composer-attachment-info { flex: 1; min-width: 0; }
        .composer-attachment-name { font-size: 0.8125rem; font-weight: 500; color: hsl(var(--foreground)); overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
        .composer-attachment-type { font-size: 0.6875rem; color: hsl(var(--muted-foreground)); text-transform: uppercase; letter-spacing: 0.04em; }
        .composer-attachment-remove { padding: 0.25rem; border: 1px solid hsl(var(--border)); background: hsl(var(--background)); cursor: pointer; border-radius: 0.375rem; color: hsl(var(--muted-foreground)); display: flex; align-items: center; flex-shrink: 0; transition: all 0.15s; }
        .composer-attachment-remove:hover { background: hsl(var(--destructive) / 0.1); border-color: hsl(var(--destructive) / 0.4); color: hsl(var(--destructive)); }

        /* Composer input area */
        .composer-input-row { display: flex; align-items: flex-start; gap: 0.625rem; }
        .composer-avatar { width: 2.125rem; height: 2.125rem; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 0.8125rem; font-weight: 700; margin-top: 0.3rem; }
        .composer-avatar img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
        .composer-avatar-initials { width: 2.125rem; height: 2.125rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.8125rem; font-weight: 700; background: hsl(var(--primary) / 0.15); color: hsl(var(--primary)); flex-shrink: 0; margin-top: 0.3rem; }

        .composer-field { flex: 1; min-width: 0; background: hsl(var(--secondary) / 0.6); border: 1.5px solid hsl(var(--border)); border-radius: 1rem; padding: 0.5rem 0.75rem 0.5rem; transition: border-color 0.2s, box-shadow 0.2s; }
        .composer-field:focus-within { border-color: hsl(var(--primary) / 0.5); box-shadow: 0 0 0 3px hsl(var(--primary) / 0.08); }
        .composer-textarea { width: 100%; min-height: 2.25rem; max-height: 9rem; background: transparent; border: none; outline: none; resize: none; font-size: 0.9rem; color: hsl(var(--foreground)); line-height: 1.5; overflow-y: auto; padding: 0; font-family: inherit; }
        .composer-textarea::placeholder { color: hsl(var(--muted-foreground) / 0.7); }

        /* Toolbar */
        .composer-toolbar { display: flex; align-items: center; gap: 0.25rem; margin-top: 0.375rem; padding-top: 0.375rem; border-top: 1px solid hsl(var(--border) / 0.6); }
        .toolbar-btn { display: flex; align-items: center; gap: 0.25rem; padding: 0.3125rem 0.5rem; border-radius: 0.4375rem; border: none; background: none; cursor: pointer; font-size: 0.75rem; font-weight: 500; color: hsl(var(--muted-foreground)); transition: all 0.15s; }
        .toolbar-btn:hover { background: hsl(var(--secondary)); color: hsl(var(--foreground)); }
        .toolbar-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .toolbar-btn.active { color: hsl(var(--primary)); background: hsl(var(--primary) / 0.1); }
        .toolbar-spacer { flex: 1; }

        /* Send button */
        .composer-send-btn { display: flex; align-items: center; gap: 0.375rem; padding: 0.5rem 1rem; border-radius: 0.625rem; font-size: 0.875rem; font-weight: 600; border: none; cursor: pointer; transition: all 0.15s; background: hsl(var(--primary)); color: hsl(var(--primary-foreground)); }
        .composer-send-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .composer-send-btn:not(:disabled):hover { opacity: 0.9; transform: translateY(-1px); box-shadow: 0 4px 12px hsl(var(--primary) / 0.3); }

        /* Upload progress */
        .upload-progress { display: flex; align-items: center; gap: 0.5rem; padding: 0.375rem 0.75rem; background: hsl(var(--primary) / 0.07); border-radius: 0.5rem; margin-bottom: 0.5rem; font-size: 0.75rem; color: hsl(var(--primary)); font-weight: 500; }
        .upload-spinner { width: 1rem; height: 1rem; border: 2px solid hsl(var(--primary) / 0.3); border-top-color: hsl(var(--primary)); border-radius: 50%; animation: spin 0.7s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* ── Emoji Picker ── */
        .community-emoji-picker { position: absolute; bottom: calc(100% + 0.5rem); left: 1rem; z-index: 50; background: hsl(var(--popover, var(--background))); border: 1px solid hsl(var(--border)); border-radius: 1rem; box-shadow: 0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08); width: 18rem; overflow: hidden; animation: popIn 0.15s ease; }
        @keyframes popIn { from { opacity: 0; transform: translateY(6px) scale(0.97); } to { opacity: 1; transform: none; } }
        .emoji-category-tabs { display: flex; padding: 0.5rem 0.5rem 0; gap: 0.25rem; border-bottom: 1px solid hsl(var(--border)); }
        .emoji-cat-tab { flex: 1; padding: 0.375rem; border: none; background: none; cursor: pointer; border-radius: 0.375rem; font-size: 1.1rem; transition: background 0.12s; }
        .emoji-cat-tab:hover { background: hsl(var(--secondary)); }
        .emoji-cat-tab.active { background: hsl(var(--primary) / 0.12); }
        .emoji-grid { display: grid; grid-template-columns: repeat(8, 1fr); gap: 0.125rem; padding: 0.5rem; }
        .emoji-btn { padding: 0.375rem; border: none; background: none; cursor: pointer; font-size: 1.25rem; border-radius: 0.375rem; transition: background 0.12s, transform 0.1s; line-height: 1; display: flex; align-items: center; justify-content: center; }
        .emoji-btn:hover { background: hsl(var(--secondary)); transform: scale(1.2); }
        .emoji-cat-label { text-align: center; font-size: 0.625rem; color: hsl(var(--muted-foreground)); padding: 0.25rem; border-top: 1px solid hsl(var(--border)); background: hsl(var(--secondary) / 0.3); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }

        /* ── Mention Dropdown ── */
        .mention-dropdown { position: absolute; bottom: calc(100% + 0.5rem); left: 1rem; right: 1rem; z-index: 49; background: hsl(var(--popover, var(--background))); border: 1px solid hsl(var(--border)); border-radius: 0.75rem; box-shadow: 0 8px 24px rgba(0,0,0,0.12); overflow: hidden; animation: popIn 0.12s ease; max-height: 14rem; overflow-y: auto; }
        .mention-item { width: 100%; display: flex; align-items: center; gap: 0.625rem; padding: 0.5rem 0.75rem; border: none; background: none; cursor: pointer; text-align: left; transition: background 0.12s; }
        .mention-item:hover { background: hsl(var(--secondary)); }
        .mention-avatar { width: 2rem; height: 2rem; border-radius: 50%; background: hsl(var(--primary) / 0.12); color: hsl(var(--primary)); font-weight: 700; font-size: 0.75rem; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .mention-avatar-all { background: hsl(var(--primary) / 0.2); font-size: 1rem; font-weight: 800; }
        .mention-info { display: flex; flex-direction: column; min-width: 0; }
        .mention-name { font-size: 0.875rem; font-weight: 600; color: hsl(var(--foreground)); }
        .mention-handle { font-size: 0.75rem; color: hsl(var(--muted-foreground)); }

        /* ── Admin panel ── */
        .admin-panel { display: none; }
        @media (min-width: 1280px) { .admin-panel { display: block; width: 22rem; min-height: 0; border-left: 1px solid hsl(var(--border)); padding: 1rem; overflow-y: auto; } }

        /* ── Mobile select ── */
        @media (max-width: 767px) {
          .composer-attachment-preview { padding: 0.375rem 0.625rem; }
          .composer-field { padding: 0.4375rem 0.625rem; }
          .community-composer { padding: 0.625rem 0.75rem; }
        }
      `}</style>

      <DashboardLayout>
        <div className="community-layout">
          {/* Course Sidebar */}
          <div className="community-sidebar">
            <p className="sidebar-label">{courseSelectorLabel} Courses</p>
            {courseOptions.map((course) => (
              <button
                key={course.id}
                onClick={() => setSearchParams({ courseId: course.id })}
                className={cn("sidebar-course-btn", course.id === resolvedCourseId && "active")}
              >
                <Hash size={13} style={{ flexShrink: 0 }} />
                {course.title}
              </button>
            ))}
          </div>

          {/* Community main */}
          <div className="community-main">
            {/* Header */}
            <div className="community-header">
              <div className="community-header-title">
                <Hash size={16} style={{ color: "hsl(var(--muted-foreground))", flexShrink: 0 }} />
                <h2>{activeCourseTitle} Community</h2>
              </div>
              {courseOptions.length > 1 && (
                <div className="md:hidden">
                  <Select
                    value={resolvedCourseId ?? ""}
                    onValueChange={(val) => setSearchParams({ courseId: val })}
                  >
                    <SelectTrigger className="w-[140px] sm:w-[180px] h-9 text-xs">
                      <SelectValue placeholder="Select course" />
                    </SelectTrigger>
                    <SelectContent>
                      {courseOptions.map((course) => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Feed */}
            <div ref={postsContainerRef} className="community-feed">
              {rootPosts.length === 0 ? (
                <div className="feed-empty">
                  <div className="feed-empty-icon">
                    <Hash size={24} style={{ color: "hsl(var(--muted-foreground))" }} />
                  </div>
                  <p style={{ fontWeight: 600, color: "hsl(var(--foreground))" }}>No messages yet</p>
                  <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>
                    Be the first to start the conversation!
                  </p>
                </div>
              ) : (
                rootPosts.map((post) => renderPost(post, false))
              )}
            </div>

            {/* Composer */}
            <div className="community-composer" ref={composerContainerRef}>
              {/* Emoji picker (portal-like, positioned above composer) */}
              {showEmojiPicker && (
                <EmojiPicker
                  onSelect={insertEmoji}
                  onClose={() => setShowEmojiPicker(false)}
                />
              )}

              {/* Mention dropdown */}
              {mentionQuery !== null && (
                <MentionDropdown
                  query={mentionQuery}
                  candidates={mentionCandidates}
                  onSelect={insertMention}
                  onClose={() => setMentionQuery(null)}
                  showAll={true}
                />
              )}

              {/* Upload progress */}
              {isUploadingAttachment && (
                <div className="upload-progress">
                  <div className="upload-spinner" role="status" aria-label="Uploading..." />
                  <span>Uploading attachment…</span>
                </div>
              )}

              {/* Reply banner */}
              {replyTarget && (
                <div className="reply-banner">
                  <CornerDownLeft size={13} className="reply-banner-icon" />
                  <p className="reply-banner-text">
                    Replying to{" "}
                    <span className="reply-banner-name">{getDisplayName(replyTarget)}</span>
                    {replyTarget.content && (
                      <>: {replyTarget.content.slice(0, 60)}{replyTarget.content.length > 60 ? "…" : ""}</>
                    )}
                  </p>
                  <button
                    type="button"
                    className="reply-banner-close"
                    aria-label="Cancel reply"
                    onClick={() => setReplyTarget(null)}
                  >
                    <X size={13} />
                  </button>
                </div>
              )}

              {/* Attachment preview */}
              {attachmentFile && !isUploadingAttachment && (
                <div className="composer-attachment-preview">
                  <div className="composer-attachment-thumb">
                    {attachmentPreviewType === "IMAGE" && attachmentPreviewUrl ? (
                      <img src={attachmentPreviewUrl} alt="Preview" />
                    ) : attachmentPreviewType === "VIDEO" ? (
                      <Film size={20} className="composer-attachment-icon" />
                    ) : attachmentPreviewType === "PDF" ? (
                      <FileText size={20} className="composer-attachment-icon" />
                    ) : attachmentPreviewType === "AUDIO" ? (
                      <Music size={20} className="composer-attachment-icon" />
                    ) : (
                      <Paperclip size={20} className="composer-attachment-icon" />
                    )}
                  </div>
                  <div className="composer-attachment-info">
                    <p className="composer-attachment-name">{attachmentFile.name}</p>
                    <p className="composer-attachment-type">{attachmentPreviewType ?? "File"}</p>
                  </div>
                  <button
                    type="button"
                    aria-label="Remove attachment"
                    className="composer-attachment-remove"
                    onClick={clearAttachment}
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Input row */}
              <div className="composer-input-row">
                {/* My avatar */}
                {myAvatarImg ? (
                  <div className="composer-avatar">
                    <img src={myAvatarImg} alt="You" />
                  </div>
                ) : (
                  <div className="composer-avatar-initials" aria-hidden="true">
                    {myInitial}
                  </div>
                )}

                {/* Composer field */}
                <div className="composer-field">
                  <textarea
                    ref={textareaRef}
                    id="community-composer-input"
                    className="composer-textarea"
                    value={content}
                    onChange={(e) => handleContentChange(e.target.value)}
                    placeholder={replyTarget ? "Write a reply…" : "What's on your mind?"}
                    rows={1}
                    aria-label="Message composer"
                    aria-multiline="true"
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setMentionQuery(null);
                        setShowEmojiPicker(false);
                        if (replyTarget) setReplyTarget(null);
                      }
                      if (e.key === "Enter" && !e.shiftKey && mentionQuery === null) {
                        e.preventDefault();
                        void onSend();
                      }
                    }}
                  />

                  {/* Toolbar */}
                  <div className="composer-toolbar">
                    {/* Emoji */}
                    <button
                      type="button"
                      aria-label="Open emoji picker"
                      aria-expanded={showEmojiPicker}
                      className={cn("toolbar-btn", showEmojiPicker && "active")}
                      disabled={!isConnected || isUploadingAttachment}
                      onClick={() => {
                        setShowEmojiPicker((v) => !v);
                        setMentionQuery(null);
                      }}
                    >
                      <SmilePlus size={15} />
                      <span className="hidden sm:inline">Emoji</span>
                    </button>

                    {/* Attachment */}
                    <input
                      ref={attachmentInputRef}
                      type="file"
                      className="hidden"
                      accept="image/*,video/*,application/pdf,audio/*"
                      aria-label="Attach file"
                      onChange={(e) => {
                        const next = e.target.files?.[0] ?? null;
                        e.currentTarget.value = "";
                        if (!next) { clearAttachment(); return; }
                        if (attachmentPreviewUrl) URL.revokeObjectURL(attachmentPreviewUrl);
                        const inferred = inferAttachmentTypeFromFile(next);
                        setAttachmentFile(next);
                        setAttachmentPreviewType(inferred);
                        setAttachmentPreviewUrl(inferred === "IMAGE" ? URL.createObjectURL(next) : null);
                      }}
                    />
                    <button
                      type="button"
                      aria-label="Attach file"
                      className={cn("toolbar-btn", attachmentFile && "active")}
                      disabled={!isConnected || isUploadingAttachment}
                      onClick={() => attachmentInputRef.current?.click()}
                    >
                      <Paperclip size={15} />
                      <span className="hidden sm:inline">Attach</span>
                    </button>

                    {/* Mention */}
                    <button
                      type="button"
                      aria-label="Mention a user"
                      className={cn("toolbar-btn", mentionQuery !== null && "active")}
                      disabled={!isConnected || isUploadingAttachment}
                      onClick={() => {
                        setShowEmojiPicker(false);
                        const el = textareaRef.current;
                        if (el) {
                          const newVal = insertAtCursor(el, "@");
                          setContent(newVal);
                          setMentionQuery("");
                          setTimeout(() => {
                            el.focus();
                            const pos = (el.selectionStart ?? 0) + 1;
                            el.setSelectionRange(pos, pos);
                          }, 0);
                        }
                      }}
                    >
                      <AtSign size={15} />
                      <span className="hidden sm:inline">Mention</span>
                    </button>

                    <div className="toolbar-spacer" />

                    {/* Send */}
                    <button
                      type="button"
                      id="community-send-btn"
                      aria-label="Send message"
                      className="composer-send-btn"
                      disabled={!canSend}
                      onClick={() => void onSend()}
                    >
                      <Send size={15} />
                      <span>{isUploadingAttachment ? "Uploading…" : "Send"}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Member Management */}
          {isAdmin && (
            <div className="admin-panel">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-4 px-2">
                Manage community
              </h3>

              <div className="space-y-4">
                <KCard className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-sm">Members</p>
                    <span className="text-xs text-muted-foreground">{members.length}</span>
                  </div>

                  <input
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Search member..."
                    className="w-full bg-background border border-input rounded-lg px-3 py-2 text-sm mb-3"
                    aria-label="Search members"
                  />

                  {communityMembersQuery.isLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ) : members.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No members yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {members
                        .filter((m) => {
                          const q = memberSearch.trim().toLowerCase();
                          if (!q) return true;
                          const emailPrefix = m.email.split("@")[0]?.toLowerCase() ?? "";
                          return emailPrefix.includes(q) || m.email.toLowerCase().includes(q);
                        })
                        .map((m) => (
                          <div
                            key={m.userId}
                            className="flex items-center justify-between gap-2 border border-border rounded-lg px-2 py-2"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{m.email.split("@")[0]}</p>
                              <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                            </div>
                            <button
                              type="button"
                              className="text-xs text-destructive hover:text-destructive/80 shrink-0"
                              onClick={async () => {
                                if (!resolvedCourseId) return;
                                try {
                                  await removeCourseCommunityMember(resolvedCourseId, m.userId);
                                  await communityMembersQuery.refetch();
                                } catch (e: any) {
                                  toast.error(e?.message ?? "Failed to remove member");
                                }
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </KCard>

                <KCard className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-sm">Add member</p>
                    <span className="text-xs text-muted-foreground">{candidates.length}</span>
                  </div>

                  <input
                    value={candidateSearch}
                    onChange={(e) => setCandidateSearch(e.target.value)}
                    placeholder="Search user..."
                    className="w-full bg-background border border-input rounded-lg px-3 py-2 text-sm mb-3"
                    aria-label="Search users to add"
                  />

                  {courseStudentsQuery.isLoading || courseTeachersQuery.isLoading || communityMembersQuery.isLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ) : (
                    <>
                      <label className="text-xs text-muted-foreground block mb-2 px-1">
                        Choose from enrolled students + assigned teachers
                      </label>
                      <select
                        className="w-full bg-background border border-input rounded-lg px-3 py-2 text-sm"
                        value={candidateUserId ?? ""}
                        aria-label="Select user to add"
                        onChange={(e) => {
                          const v = e.target.value;
                          setCandidateUserId(v ? Number(v) : null);
                        }}
                      >
                        <option value="">Select user...</option>
                        {candidates
                          .filter((c) => {
                            const q = candidateSearch.trim().toLowerCase();
                            if (!q) return true;
                            const emailPrefix = c.email.split("@")[0]?.toLowerCase() ?? "";
                            return emailPrefix.includes(q) || c.email.toLowerCase().includes(q);
                          })
                          .map((c) => (
                            <option key={c.userId} value={c.userId}>
                              {c.email.split("@")[0]}
                            </option>
                          ))}
                      </select>

                      <Button
                        type="button"
                        className="mt-3 w-full"
                        disabled={!candidateUserId}
                        onClick={async () => {
                          if (!resolvedCourseId || !candidateUserId) return;
                          try {
                            await addCourseCommunityMember(resolvedCourseId, candidateUserId);
                            await communityMembersQuery.refetch();
                            setCandidateUserId(null);
                            toast.success("Member added");
                          } catch (e: any) {
                            toast.error(e?.message ?? "Failed to add member");
                          }
                        }}
                      >
                        Add
                      </Button>

                      {candidates.length === 0 && (
                        <p className="text-xs text-muted-foreground mt-2">Everyone eligible is already a member.</p>
                      )}
                    </>
                  )}
                </KCard>
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={Boolean(deleteConfirmTarget)}
          onOpenChange={(open) => {
            if (!open) {
              setDeleteConfirmTarget(null);
              setIsDeletingPost(false);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete message?</DialogTitle>
              <DialogDescription>
                This will permanently remove the message from the community.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => { setDeleteConfirmTarget(null); }}
                disabled={isDeletingPost}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  const target = deleteConfirmTarget;
                  if (!target) return;
                  setIsDeletingPost(true);
                  try {
                    await onDeletePost(target);
                    setDeleteConfirmTarget(null);
                  } finally {
                    setIsDeletingPost(false);
                  }
                }}
                disabled={isDeletingPost}
              >
                {isDeletingPost ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog
          open={Boolean(editTarget)}
          onOpenChange={(open) => {
            if (!open) {
              setEditTarget(null);
              setIsSavingEdit(false);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit message</DialogTitle>
              <DialogDescription>
                Change the text of your message. Attachments are unchanged.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="min-h-[120px] resize-y"
              placeholder="Message..."
            />
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditTarget(null)}
                disabled={isSavingEdit}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void onSaveEdit()}
                disabled={isSavingEdit || !editContent.trim()}
              >
                {isSavingEdit ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    </>
  );
}
